import './dashboard.css'
import { reportPageView } from './pageview.js'
import { buildLoginUrl } from './auth-state.js'
import { capabilityEnabled } from './rollout.js'
import { getLocale, localeHeaderValue, onLocaleChange } from './locale.js'
import { getMessages } from './i18n.js'
import { showConfirmDialog } from './dialog.js'

const statusEl = document.getElementById('education-status')
const classForm = document.getElementById('class-form')
const joinForm = document.getElementById('join-form')
const assignmentForm = document.getElementById('assignment-form')
const classSettingsForm = document.getElementById('class-settings-form')
const assignmentIdInput = document.getElementById('assignment-id')
const submissionForm = document.getElementById('submission-form')
const classPicker = document.getElementById('class-picker')
const assignmentClass = document.getElementById('assignment-class')
const classSummary = document.getElementById('class-summary')
const studentsBody = document.getElementById('students-body')
const assignmentsBody = document.getElementById('assignments-body')
const studentClassPicker = document.getElementById('student-class-picker')
const studentClassOverviewPicker = document.getElementById('student-class-overview-picker')
const studentAssignmentPicker = document.getElementById('student-assignment-picker')
const studentProjectPicker = document.getElementById('student-project-picker')
const studentFeedbackInput = document.getElementById('student-feedback')
const assignmentDueAtInput = document.getElementById('education-input-due-at')
const assignmentStatusSelect = document.getElementById('education-assignment-status')
const resetAssignmentBtn = document.getElementById('education-reset-assignment')
const classSettingsNameInput = document.getElementById('education-settings-name')
const classSettingsTermInput = document.getElementById('education-settings-term')
const classSettingsStatusSelect = document.getElementById('education-settings-status')
const resetInviteBtn = document.getElementById('education-reset-invite')
const studentClassSummary = document.getElementById('student-class-summary')
const studentAssignmentsBody = document.getElementById('student-assignments-body')
const teacherSubmissionsBody = document.getElementById('teacher-submissions-body')
const teacherSubmissionAssignmentLabel = document.getElementById('teacher-submission-assignment-label')

const createClassPanel = document.getElementById('education-create-class-panel')
const createAssignmentPanel = document.getElementById('education-create-assignment-panel')
const teacherOverviewSection = document.getElementById('education-teacher-overview-section')
const teacherDetailSection = document.getElementById('education-teacher-detail-section')
const teacherSubmissionsSection = document.getElementById('education-teacher-submissions-section')

let classesCache = []
let teacherOverviewCache = null
let studentAssignmentsCache = null
let projectsCache = []
let capabilitySnapshot = {}
let editingAssignmentId = null

function t() {
  return getMessages('pages/education', getLocale())
}

function setStatus(text, isError = false) {
  statusEl.textContent = text
  statusEl.style.color = isError ? '#c2410c' : ''
}

function formatDate(ts) {
  return ts ? new Date(ts * 1000).toLocaleString(getLocale() === 'en' ? 'en-US' : 'zh-CN', { hour12: false }) : '-'
}

function formatDateTimeLocal(ts) {
  if (!ts) return ''
  const date = new Date(ts * 1000)
  const pad = (value) => String(value).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

function assignmentStatusLabel(status) {
  return t().assignmentStatuses?.[status] || status
}

function assignmentActionLabel(item) {
  if (item.status !== 'published') return assignmentStatusLabel(item.status)
  if (item.due_at && (item.due_at * 1000) <= Date.now()) return t().assignmentExpired
  return t().assignmentOpen
}

function resetAssignmentEditor() {
  editingAssignmentId = null
  assignmentIdInput.value = ''
  assignmentForm.reset()
  assignmentClass.disabled = false
  assignmentStatusSelect.value = 'published'
  resetAssignmentBtn.hidden = true
  document.getElementById('education-title-assignment').textContent = t().ui.createAssignment
  document.getElementById('education-submit-assignment').textContent = t().ui.publishButton
  if (teacherClasses().length) {
    assignmentClass.value = assignmentClass.value || teacherClasses()[0].id
  }
}

function openAssignmentEditor(assignment) {
  editingAssignmentId = assignment.id
  assignmentIdInput.value = assignment.id
  assignmentClass.value = teacherOverviewCache?.class?.id || assignmentClass.value
  assignmentClass.disabled = true
  assignmentForm.elements.title.value = assignment.title || ''
  assignmentForm.elements.description.value = assignment.description || ''
  assignmentDueAtInput.value = formatDateTimeLocal(assignment.due_at)
  assignmentStatusSelect.value = assignment.status || 'published'
  resetAssignmentBtn.hidden = false
  document.getElementById('education-title-assignment').textContent = t().ui.editAssignment
  document.getElementById('education-submit-assignment').textContent = t().ui.saveAssignmentButton
}

function escapeHtml(value) {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;')
}

function teacherClasses() {
  return classesCache.filter((item) => item.member_role === 'teacher')
}

function studentClasses() {
  return classesCache.filter((item) => item.member_role === 'student')
}

function currentStudentAssignment() {
  const assignmentId = studentAssignmentPicker.value
  return studentAssignmentsCache?.assignments?.find((item) => item.id === assignmentId) || null
}

async function fetchJson(url, options = {}) {
  const res = await fetch(url, {
    credentials: 'include',
    headers: {
      'Accept-Language': localeHeaderValue(),
      ...(options.headers || {}),
    },
    ...options,
  })
  const body = await res.json().catch(() => ({}))
  if (res.status === 401) {
    window.location.href = buildLoginUrl('/education')
    return null
  }
  if (!res.ok) throw new Error(body.detail || t().requestFailed)
  return body
}

async function loadCapabilitySnapshot() {
  const body = await fetchJson('/api/account/summary')
  if (!body) return
  capabilitySnapshot = body.billing?.capabilities || {}
  renderTeacherVisibility()
}

async function loadProjects() {
  const body = await fetchJson('/api/projects')
  if (!body) return
  projectsCache = body.projects || []
  studentProjectPicker.innerHTML = projectsCache.length
    ? projectsCache.map((item) => `<option value="${item.id}">${escapeHtml(item.name)}</option>`).join('')
    : `<option value="">${escapeHtml(t().emptyProjects)}</option>`
  studentProjectPicker.disabled = !projectsCache.length
}

function renderClassOptions() {
  const teacherOptions = teacherClasses().map((item) => `<option value="${item.id}">${escapeHtml(item.name)}</option>`).join('')
  const studentOptions = studentClasses().map((item) => `<option value="${item.id}">${escapeHtml(item.name)}</option>`).join('')
  classPicker.innerHTML = teacherOptions
  assignmentClass.innerHTML = teacherOptions
  studentClassPicker.innerHTML = studentOptions
  studentClassOverviewPicker.innerHTML = studentOptions
}

function renderTeacherVisibility() {
  const teacherEnabled = capabilityEnabled(capabilitySnapshot, 'classroom_management', false)
  const hasTeacherRole = teacherClasses().length > 0
  const showTeacher = teacherEnabled || hasTeacherRole
  createClassPanel.hidden = !showTeacher
  createAssignmentPanel.hidden = !showTeacher
  teacherOverviewSection.hidden = !hasTeacherRole
  teacherDetailSection.hidden = !hasTeacherRole
  teacherSubmissionsSection.hidden = !hasTeacherRole
  if (!showTeacher && !studentClasses().length) {
    setStatus(t().studentWelcome)
  }
}

function renderStudentSummary() {
  const text = t()
  if (!studentAssignmentsCache) {
    studentClassSummary.innerHTML = `<div class="empty">${text.emptyStudentClasses}</div>`
    return
  }
  studentClassSummary.innerHTML = `
    <div class="kv-row"><span class="muted">${escapeHtml(text.studentSummaryLabels[0])}</span><strong>${escapeHtml(studentAssignmentsCache.class.name)}</strong></div>
    <div class="kv-row"><span class="muted">${escapeHtml(text.studentSummaryLabels[1])}</span><strong>${escapeHtml(studentAssignmentsCache.class.term || '-')}</strong></div>
    <div class="kv-row"><span class="muted">${escapeHtml(text.studentSummaryLabels[2])}</span><strong>${escapeHtml(studentAssignmentsCache.assignments.length)}</strong></div>
  `
}

function renderStudentAssignments() {
  const text = t()
  const assignments = studentAssignmentsCache?.assignments || []
  if (!assignments.length) {
    studentAssignmentsBody.innerHTML = `<tr><td colspan="6" class="empty">${escapeHtml(text.emptyAssignments)}</td></tr>`
    return
  }
  studentAssignmentsBody.innerHTML = assignments.map((item) => `
    <tr>
      <td>${escapeHtml(item.title)}</td>
      <td>${escapeHtml(assignmentStatusLabel(item.status))}</td>
      <td>${escapeHtml(formatDate(item.due_at))}</td>
      <td>${escapeHtml(item.my_submission?.status ? assignmentStatusLabel(item.my_submission.status) : text.notSubmitted)}</td>
      <td>${escapeHtml(formatDate(item.my_submission?.submitted_at))}</td>
      <td><button class="inline-action" type="button" data-assignment-select="${escapeHtml(item.id)}" ${item.status !== 'published' || (item.due_at && (item.due_at * 1000) <= Date.now()) ? 'disabled' : ''}>${escapeHtml(item.status === 'published' ? assignmentActionLabel(item) : assignmentStatusLabel(item.status))}</button></td>
    </tr>
  `).join('')
}

function renderTeacherOverview() {
  const text = t()
  if (!teacherOverviewCache) {
    classSummary.innerHTML = `<div class="empty">${escapeHtml(text.emptyClasses)}</div>`
    studentsBody.innerHTML = `<tr><td colspan="2" class="empty">${escapeHtml(text.emptyStudents)}</td></tr>`
    assignmentsBody.innerHTML = `<tr><td colspan="5" class="empty">${escapeHtml(text.emptyAssignments)}</td></tr>`
    teacherSubmissionsBody.innerHTML = `<tr><td colspan="5" class="empty">${escapeHtml(text.emptySubmissions)}</td></tr>`
    return
  }
  const labels = text.summaryLabels
  classSummary.innerHTML = `
    <div class="kv-row"><span class="muted">${escapeHtml(labels[0])}</span><strong>${escapeHtml(teacherOverviewCache.class.name)}</strong></div>
    <div class="kv-row"><span class="muted">${escapeHtml(labels[1])}</span><strong>${escapeHtml(teacherOverviewCache.class.invite_code)}</strong></div>
    <div class="kv-row"><span class="muted">${escapeHtml(labels[2])}</span><strong>${escapeHtml(teacherOverviewCache.summary.student_count)}</strong></div>
    <div class="kv-row"><span class="muted">${escapeHtml(labels[3])}</span><strong>${escapeHtml(teacherOverviewCache.summary.assignment_count)}</strong></div>
    <div class="kv-row"><span class="muted">${escapeHtml(labels[4])}</span><strong>${escapeHtml(teacherOverviewCache.summary.submission_count)}</strong></div>
  `
  classSettingsNameInput.value = teacherOverviewCache.class.name || ''
  classSettingsTermInput.value = teacherOverviewCache.class.term || ''
  classSettingsStatusSelect.value = teacherOverviewCache.class.status || 'active'
  studentsBody.innerHTML = teacherOverviewCache.students?.length
    ? teacherOverviewCache.students.map((item) => `<tr><td>${escapeHtml(item.email || item.user_id)}</td><td>${escapeHtml(formatDate(item.joined_at))}</td><td><button class="inline-action danger" type="button" data-student-remove="${escapeHtml(item.user_id)}" data-student-email="${escapeHtml(item.email || item.user_id)}">${escapeHtml(text.removeStudent)}</button></td></tr>`).join('')
    : `<tr><td colspan="3" class="empty">${escapeHtml(text.emptyStudents)}</td></tr>`
  assignmentsBody.innerHTML = teacherOverviewCache.assignments?.length
    ? teacherOverviewCache.assignments.map((item) => `<tr><td>${escapeHtml(item.title)}</td><td>${escapeHtml(assignmentStatusLabel(item.status))}</td><td>${escapeHtml(formatDate(item.due_at))}</td><td>${escapeHtml(item.submitted_count)}</td><td>${escapeHtml(item.student_count)}</td><td><div class="inline-actions"><button class="inline-action" type="button" data-teacher-assignment="${escapeHtml(item.id)}" data-teacher-assignment-title="${escapeHtml(item.title)}">${escapeHtml(text.viewSubmissions)}</button><button class="inline-action" type="button" data-assignment-edit="${escapeHtml(item.id)}">${escapeHtml(text.editAssignment)}</button><button class="inline-action danger" type="button" data-assignment-delete="${escapeHtml(item.id)}" data-assignment-delete-title="${escapeHtml(item.title)}">${escapeHtml(text.deleteAssignment)}</button></div></td></tr>`).join('')
    : `<tr><td colspan="6" class="empty">${escapeHtml(text.emptyAssignments)}</td></tr>`
}

function populateStudentAssignmentOptions() {
  const assignments = studentAssignmentsCache?.assignments || []
  studentAssignmentPicker.innerHTML = assignments.length
    ? assignments.map((item) => `<option value="${item.id}">${escapeHtml(item.title)}</option>`).join('')
    : `<option value="">${escapeHtml(t().emptyAssignments)}</option>`
  studentAssignmentPicker.disabled = !assignments.length
  syncSubmissionFormFromCurrentAssignment()
}

function syncSubmissionFormFromCurrentAssignment() {
  const assignment = currentStudentAssignment()
  if (!assignment) {
    studentFeedbackInput.value = ''
    return
  }
  studentFeedbackInput.value = assignment.my_submission?.feedback || ''
  if (assignment.my_submission?.project_id) {
    studentProjectPicker.value = assignment.my_submission.project_id
  } else if (projectsCache[0]) {
    studentProjectPicker.value = projectsCache[0].id
  }
}

async function loadClasses() {
  const previousTeacherClassId = classPicker.value || assignmentClass.value
  const previousStudentClassId = studentClassOverviewPicker.value || studentClassPicker.value
  const body = await fetchJson('/api/education/classes')
  if (!body) return
  classesCache = body.classes || []
  renderClassOptions()
  renderTeacherVisibility()
  if (teacherClasses().length) {
    const nextTeacherClassId = teacherClasses().some((item) => item.id === previousTeacherClassId)
      ? previousTeacherClassId
      : teacherClasses()[0].id
    await loadTeacherOverview(nextTeacherClassId)
  } else {
    renderTeacherOverview()
  }
  if (studentClasses().length) {
    const initialClassId = studentClasses().some((item) => item.id === previousStudentClassId)
      ? previousStudentClassId
      : studentClasses()[0].id
    await loadStudentAssignments(initialClassId)
    studentClassPicker.value = initialClassId
    studentClassOverviewPicker.value = initialClassId
  } else {
    studentAssignmentsCache = null
    renderStudentSummary()
    renderStudentAssignments()
    populateStudentAssignmentOptions()
  }
}

async function loadTeacherOverview(classId) {
  if (!classId) {
    teacherOverviewCache = null
    renderTeacherOverview()
    return
  }
  const body = await fetchJson(`/api/education/classes/${classId}/overview`)
  if (!body) return
  teacherOverviewCache = body
  classPicker.value = classId
  assignmentClass.value = classId
  renderTeacherOverview()
}

async function loadStudentAssignments(classId) {
  if (!classId) {
    studentAssignmentsCache = null
    renderStudentSummary()
    renderStudentAssignments()
    populateStudentAssignmentOptions()
    return
  }
  const body = await fetchJson(`/api/education/classes/${classId}/assignments`)
  if (!body) return
  studentAssignmentsCache = body
  studentClassPicker.value = classId
  studentClassOverviewPicker.value = classId
  renderStudentSummary()
  renderStudentAssignments()
  populateStudentAssignmentOptions()
}

async function loadTeacherAssignmentSubmissions(assignmentId, assignmentTitle = '') {
  const body = await fetchJson(`/api/education/assignments/${assignmentId}/submissions`)
  if (!body) return
  teacherSubmissionAssignmentLabel.textContent = assignmentTitle || body.assignment.title || '—'
  teacherSubmissionsBody.innerHTML = body.submissions?.length
    ? body.submissions.map((item) => `<tr><td>${escapeHtml(item.student_email || item.student_user_id)}</td><td>${escapeHtml(item.status)}</td><td>${escapeHtml(item.project_id || '-')}</td><td>${escapeHtml(item.feedback || '-')}</td><td>${escapeHtml(formatDate(item.submitted_at))}</td></tr>`).join('')
    : `<tr><td colspan="5" class="empty">${escapeHtml(t().emptySubmissions)}</td></tr>`
}

classForm?.addEventListener('submit', async (event) => {
  event.preventDefault()
  const formData = new FormData(classForm)
  try {
    await fetchJson('/api/education/classes', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: formData.get('name'),
        term: formData.get('term'),
      }),
    })
    classForm.reset()
    await loadClasses()
    setStatus(t().createdClass)
  } catch (error) {
    setStatus(error.message || t().createClassFailed, true)
  }
})

joinForm?.addEventListener('submit', async (event) => {
  event.preventDefault()
  const formData = new FormData(joinForm)
  try {
    await fetchJson('/api/education/classes/join', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ invite_code: formData.get('inviteCode') }),
    })
    joinForm.reset()
    await loadClasses()
    setStatus(t().joinedClass)
  } catch (error) {
    setStatus(error.message || t().joinClassFailed, true)
  }
})

assignmentForm?.addEventListener('submit', async (event) => {
  event.preventDefault()
  const formData = new FormData(assignmentForm)
  const classId = formData.get('classId')
  try {
    const assignmentId = formData.get('assignmentId')
    await fetchJson(assignmentId ? `/api/education/assignments/${assignmentId}` : `/api/education/classes/${classId}/assignments`, {
      method: assignmentId ? 'PUT' : 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        title: formData.get('title'),
        description: formData.get('description'),
        due_at: formData.get('dueAt') ? new Date(String(formData.get('dueAt'))).toISOString() : null,
        status: formData.get('status') || 'published',
      }),
    })
    resetAssignmentEditor()
    assignmentClass.value = classId
    await loadTeacherOverview(classId)
    setStatus(assignmentId ? t().assignmentUpdated : t().assignmentPublished)
  } catch (error) {
    setStatus(error.message || (formData.get('assignmentId') ? t().updateAssignmentFailed : t().publishAssignmentFailed), true)
  }
})

submissionForm?.addEventListener('submit', async (event) => {
  event.preventDefault()
  const formData = new FormData(submissionForm)
  const assignmentId = formData.get('assignmentId')
  try {
    await fetchJson(`/api/education/assignments/${assignmentId}/submit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        project_id: formData.get('projectId') || null,
        feedback: formData.get('feedback') || null,
      }),
    })
    await loadStudentAssignments(formData.get('classId'))
    setStatus(t().submissionSaved)
  } catch (error) {
    setStatus(error.message || t().submitAssignmentFailed, true)
  }
})

classPicker?.addEventListener('change', () => {
  loadTeacherOverview(classPicker.value).catch((error) => setStatus(error.message || t().loadClassFailed, true))
})

classSettingsForm?.addEventListener('submit', async (event) => {
  event.preventDefault()
  if (!teacherOverviewCache?.class?.id) return
  const classId = teacherOverviewCache.class.id
  try {
    await fetchJson(`/api/education/classes/${classId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: classSettingsNameInput.value,
        term: classSettingsTermInput.value || null,
        status: classSettingsStatusSelect.value,
      }),
    })
    await loadClasses()
    if (teacherClasses().some((item) => item.id === classId)) {
      await loadTeacherOverview(classId)
    }
    setStatus(t().classUpdated)
  } catch (error) {
    setStatus(error.message || t().updateClassFailed, true)
  }
})

resetInviteBtn?.addEventListener('click', async () => {
  if (!teacherOverviewCache?.class?.id) return
  try {
    await fetchJson(`/api/education/classes/${teacherOverviewCache.class.id}/reset-invite`, {
      method: 'POST',
    })
    await loadTeacherOverview(teacherOverviewCache.class.id)
    setStatus(t().inviteReset)
  } catch (error) {
    setStatus(error.message || t().resetInviteFailed, true)
  }
})

studentClassPicker?.addEventListener('change', () => {
  loadStudentAssignments(studentClassPicker.value).catch((error) => setStatus(error.message || t().loadStudentAssignmentsFailed, true))
})

studentClassOverviewPicker?.addEventListener('change', () => {
  loadStudentAssignments(studentClassOverviewPicker.value).catch((error) => setStatus(error.message || t().loadStudentAssignmentsFailed, true))
})

studentAssignmentPicker?.addEventListener('change', syncSubmissionFormFromCurrentAssignment)

assignmentsBody?.addEventListener('click', (event) => {
  const button = event.target.closest('[data-teacher-assignment]')
  if (button) {
    loadTeacherAssignmentSubmissions(button.dataset.teacherAssignment, button.dataset.teacherAssignmentTitle).catch((error) => {
      setStatus(error.message || t().loadSubmissionsFailed, true)
    })
    return
  }
  const editButton = event.target.closest('[data-assignment-edit]')
  if (editButton) {
    const assignment = teacherOverviewCache?.assignments?.find((item) => item.id === editButton.dataset.assignmentEdit)
    if (assignment) openAssignmentEditor(assignment)
    return
  }
  const deleteButton = event.target.closest('[data-assignment-delete]')
  if (deleteButton) {
    const assignmentId = deleteButton.dataset.assignmentDelete
    const assignmentTitle = deleteButton.dataset.assignmentDeleteTitle
    showConfirmDialog(t().deleteAssignmentConfirm.replace('{title}', assignmentTitle), {
      title: t().deleteAssignment,
    }).then(async (confirmed) => {
      if (!confirmed) return
      try {
        await fetchJson(`/api/education/assignments/${assignmentId}`, { method: 'DELETE' })
        if (editingAssignmentId === assignmentId) resetAssignmentEditor()
        await loadTeacherOverview(classPicker.value)
        setStatus(t().assignmentDeleted)
      } catch (error) {
        setStatus(error.message || t().deleteAssignmentFailed, true)
      }
    })
  }
})

studentsBody?.addEventListener('click', (event) => {
  const button = event.target.closest('[data-student-remove]')
  if (!button || !teacherOverviewCache?.class?.id) return
  showConfirmDialog(t().removeStudentConfirm.replace('{student}', button.dataset.studentEmail), {
    title: t().removeStudent,
  }).then(async (confirmed) => {
    if (!confirmed) return
    try {
      await fetchJson(`/api/education/classes/${teacherOverviewCache.class.id}/students/${button.dataset.studentRemove}`, {
        method: 'DELETE',
      })
      await loadTeacherOverview(teacherOverviewCache.class.id)
      setStatus(t().studentRemoved)
    } catch (error) {
      setStatus(error.message || t().removeStudentFailed, true)
    }
  })
})

studentAssignmentsBody?.addEventListener('click', (event) => {
  const button = event.target.closest('[data-assignment-select]')
  if (!button) return
  studentAssignmentPicker.value = button.dataset.assignmentSelect
  syncSubmissionFormFromCurrentAssignment()
})

resetAssignmentBtn?.addEventListener('click', () => {
  resetAssignmentEditor()
})

function applyLocalizedText() {
  document.title = t().title
  document.querySelector('.dashboard-brand-text')?.replaceChildren(document.createTextNode(t().brandName || '天元计算'))
  document.getElementById('education-link-account').textContent = t().account
  document.getElementById('education-link-workspace').textContent = t().workspace
  const ui = t().ui
  document.getElementById('education-title-join').textContent = ui.joinClass
  document.getElementById('education-label-invite-code').childNodes[0].textContent = ui.inviteCode
  document.getElementById('education-submit-join').textContent = ui.joinButton
  document.getElementById('education-title-class').textContent = ui.createClass
  document.getElementById('education-label-class-name').childNodes[0].textContent = ui.className
  document.getElementById('education-label-term').childNodes[0].textContent = ui.term
  document.getElementById('education-label-class-settings-name').childNodes[0].textContent = ui.className
  document.getElementById('education-label-class-settings-term').childNodes[0].textContent = ui.term
  document.getElementById('education-label-class-settings-status').childNodes[0].textContent = ui.status
  document.querySelector('#education-settings-status option[value="active"]').textContent = ui.classStatuses.active
  document.querySelector('#education-settings-status option[value="archived"]').textContent = ui.classStatuses.archived
  document.getElementById('education-input-term').placeholder = ui.termPlaceholder
  document.getElementById('education-submit-class').textContent = ui.createClassButton
  document.getElementById('education-submit-class-settings').textContent = ui.saveClassButton
  document.getElementById('education-reset-invite').textContent = ui.resetInviteButton
  document.getElementById('education-title-assignment').textContent = ui.createAssignment
  document.getElementById('education-label-assignment-class').childNodes[0].textContent = ui.classLabel
  document.getElementById('education-label-title').childNodes[0].textContent = ui.titleLabel
  document.getElementById('education-label-description').childNodes[0].textContent = ui.descriptionLabel
  document.getElementById('education-input-description').placeholder = ui.descriptionPlaceholder
  document.getElementById('education-label-due-at').childNodes[0].textContent = ui.dueAtLabel
  document.getElementById('education-label-assignment-status').childNodes[0].textContent = ui.assignmentStatusLabel
  document.querySelector('#education-assignment-status option[value="published"]').textContent = ui.assignmentStatuses.published
  document.querySelector('#education-assignment-status option[value="closed"]').textContent = ui.assignmentStatuses.closed
  document.querySelector('#education-assignment-status option[value="archived"]').textContent = ui.assignmentStatuses.archived
  document.getElementById('education-submit-assignment').textContent = editingAssignmentId ? ui.saveAssignmentButton : ui.publishButton
  document.getElementById('education-reset-assignment').textContent = ui.resetAssignmentButton
  document.getElementById('education-title-assignment').textContent = editingAssignmentId ? ui.editAssignment : ui.createAssignment
  document.getElementById('education-title-submit').textContent = ui.submitAssignment
  document.getElementById('education-label-student-class').childNodes[0].textContent = ui.classLabel
  document.getElementById('education-label-student-assignment').childNodes[0].textContent = ui.assignmentLabel
  document.getElementById('education-label-student-project').childNodes[0].textContent = ui.projectLabel
  document.getElementById('education-label-student-feedback').childNodes[0].textContent = ui.feedbackLabel
  document.getElementById('student-feedback').placeholder = ui.feedbackPlaceholder
  document.getElementById('education-submit-student').textContent = ui.submitButton
  document.getElementById('education-title-overview').textContent = ui.myClasses
  document.getElementById('education-copy-overview').textContent = ui.overviewCopy
  document.getElementById('education-title-student-classes').textContent = ui.studentClasses
  document.getElementById('education-copy-student').textContent = ui.studentCopy
  document.getElementById('education-title-student-assignments').textContent = ui.studentAssignments
  document.getElementById('education-title-students').textContent = ui.students
  document.getElementById('education-th-student-email').textContent = ui.email
  document.getElementById('education-th-student-joined').textContent = ui.joinedAt
  document.getElementById('education-th-student-action').textContent = ui.action
  document.getElementById('education-title-progress').textContent = ui.assignmentProgress
  document.getElementById('education-th-assignment-title').textContent = ui.titleLabel
  document.getElementById('education-th-assignment-status').textContent = ui.status
  document.getElementById('education-th-assignment-due').textContent = ui.dueAtColumn
  document.getElementById('education-th-assignment-submitted').textContent = ui.submitted
  document.getElementById('education-th-assignment-total').textContent = ui.totalStudents
  document.getElementById('education-th-assignment-detail').textContent = ui.detail
  document.getElementById('education-title-submissions').textContent = ui.teacherSubmissionDetails
  document.getElementById('education-copy-submissions').textContent = ui.teacherSubmissionCopy
  document.getElementById('education-th-submission-email').textContent = ui.student
  document.getElementById('education-th-submission-status').textContent = ui.status
  document.getElementById('education-th-submission-project').textContent = ui.projectLabel
  document.getElementById('education-th-submission-feedback').textContent = ui.feedbackLabel
  document.getElementById('education-th-submission-time').textContent = ui.submittedAt
  document.getElementById('education-th-student-assignment-title').textContent = ui.titleLabel
  document.getElementById('education-th-student-assignment-status').textContent = ui.status
  document.getElementById('education-th-student-assignment-due').textContent = ui.dueAtColumn
  document.getElementById('education-th-student-assignment-submission').textContent = ui.mySubmission
  document.getElementById('education-th-student-assignment-time').textContent = ui.submittedAt
  document.getElementById('education-th-student-assignment-action').textContent = ui.action
  renderTeacherOverview()
  renderStudentSummary()
  renderStudentAssignments()
}

applyLocalizedText()
onLocaleChange(applyLocalizedText)
reportPageView('/education')
resetAssignmentEditor()

Promise.all([loadCapabilitySnapshot(), loadProjects(), loadClasses()])
  .then(async () => {
    renderTeacherVisibility()
    if (teacherOverviewCache?.assignments?.length) {
      const firstAssignment = teacherOverviewCache.assignments[0]
      await loadTeacherAssignmentSubmissions(firstAssignment.id, firstAssignment.title)
    } else {
      teacherSubmissionAssignmentLabel.textContent = '—'
      teacherSubmissionsBody.innerHTML = `<tr><td colspan="5" class="empty">${escapeHtml(t().emptySubmissions)}</td></tr>`
    }
    setStatus(t().synced)
  })
  .catch((error) => {
    setStatus(error.message || t().genericLoadFailed, true)
  })
